import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import envConfig from "../envConfig";
import Footer from "./Footer";

function Profile() {
    const { isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();

    // State for User Details
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [isEditingDetails, setIsEditingDetails] = useState(false);

    // State for Profile Picture
    const [profilePicture, setProfilePicture] = useState(null);
    const [isEditingPicture, setIsEditingPicture] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    // State for Change Password
    const [showPasswordForm, setShowPasswordForm] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [loading, setLoading] = useState(false);

    // Fetch user data on component mount
    useEffect(() => {
        if (!isAuthenticated) {
            navigate("/login");
            return;
        }

        // Fetch user details from localStorage (or API in production)
        const userDetails = JSON.parse(localStorage.getItem("userDetails")) || {};
        setFirstName(userDetails.first_name || "User");
        setLastName(userDetails.last_name || "");
        setEmail(userDetails.email || "user@example.com");
        setPhone(userDetails.phone || "");
        setProfilePicture(userDetails.profile_image || "default_user.png");
    }, [isAuthenticated, navigate]);

    // Handle User Details Update
    const handleUpdateDetails = async (e) => {
        e.preventDefault();
        if (!firstName || !lastName || !email || !phone) {
            Swal.fire({
                title: "Error",
                text: "All fields are required!",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${envConfig.API_URL}/auth/self`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: localStorage.getItem("profishBlue_authToken"),
                },
                body: JSON.stringify({
                    purpose: "details",
                    first_name: firstName,
                    last_name: lastName,
                    email,
                    phone,
                }),
            });

            const data = await response.json();
            if (response.ok && data.success) {
                Swal.fire({
                    title: "Success",
                    text: "Details updated successfully!",
                    icon: "success",
                    confirmButtonText: "OK",
                });
                // Update localStorage
                const userDetails = { ...JSON.parse(localStorage.getItem("userDetails")), first_name: firstName, last_name: lastName, email, phone };
                localStorage.setItem("userDetails", JSON.stringify(userDetails));
                setIsEditingDetails(false);
            } else {
                Swal.fire({
                    title: "Error",
                    text: data.message || "Failed to update details!",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: "An error occurred. Please try again.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle Profile Picture Selection
    const handlePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePicture(reader.result); // Preview the image
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle Profile Picture Upload
    const handleUploadPicture = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            Swal.fire({
                title: "Error",
                text: "Please select an image!",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("purpose", "profile_image");
        formData.append("profile_image", selectedFile);

        try {
            const response = await fetch(`${envConfig.API_URL}/auth/self`, {
                method: "PATCH",
                headers: {
                    Authorization: localStorage.getItem("profishBlue_authToken"),
                },
                body: formData,
            });

            const data = await response.json();
            if (response.ok && data.success) {
                Swal.fire({
                    title: "Success",
                    text: "Profile picture updated successfully!",
                    icon: "success",
                    confirmButtonText: "OK",
                });
                // Update localStorage
                const userDetails = { ...JSON.parse(localStorage.getItem("userDetails")), profile_image: data.profile_image || profilePicture };
                localStorage.setItem("userDetails", JSON.stringify(userDetails));
                setIsEditingPicture(false);
                setSelectedFile(null);
            } else {
                Swal.fire({
                    title: "Error",
                    text: data.message || "Failed to update profile picture!",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: "An error occurred. Please try again.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle Password Change
    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            Swal.fire({
                title: "Error",
                text: "Passwords do not match!",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${envConfig.API_URL}/auth/self`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: localStorage.getItem("profishBlue_authToken"),
                },
                body: JSON.stringify({
                    purpose: "reset_password",
                    current_password: currentPassword,
                    new_password: newPassword,
                }),
            });

            const data = await response.json();
            if (response.ok && data.success) {
                Swal.fire({
                    title: "Success",
                    text: "Password changed successfully!",
                    icon: "success",
                    confirmButtonText: "OK",
                });
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
                setShowPasswordForm(false);
            } else {
                Swal.fire({
                    title: "Error",
                    text: data.message || "Failed to change password!",
                    icon: "error",
                    confirmButtonText: "OK",
                });
            }
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: "An error occurred. Please try again.",
                icon: "error",
                confirmButtonText: "OK",
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value;
        if (/^\d{0,16}$/.test(value)) {
            setPhone(value);
        }
    };

    return (
        <>
            <section className="profile-section py-5 bg-white my-5">
                <div className="container px-5">
                    <div className="row px-5">
                        <div className="col-md-12">
                            {/* User Details Section */}

                            <div className="row">
                                <div className="col-sm-12 col-md-4 col-lg-4">
                                    <div className="card border-0 mb-4">
                                        <div className="card-body">
                                            <h5 className="card-title mb-3">User Details</h5>
                                            {isEditingDetails ? (
                                                <form onSubmit={handleUpdateDetails}>

                                                    <div className="row">
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">First Name</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={firstName}
                                                                    onChange={(e) => setFirstName(e.target.value)}
                                                                    required
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">Last Name</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={lastName}
                                                                    onChange={(e) => setLastName(e.target.value)}
                                                                    required
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">Email</label>
                                                                <input
                                                                    type="email"
                                                                    className="form-control"
                                                                    value={email}
                                                                    onChange={(e) => setEmail(e.target.value)}
                                                                    required
                                                                    disabled
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">Phone</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    id="phone"
                                                                    placeholder="Enter phone number with country code"
                                                                    value={phone}
                                                                    onChange={handlePhoneChange}
                                                                    maxLength="16"
                                                                    required
                                                                    pattern="^\d{1,16}$"
                                                                    title="Phone number must contain only digits, up to 16 characters."
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="d-flex gap-2 mt-4">
                                                                <button type="submit" className="btn btn-submit mt-1" disabled={loading}>
                                                                    {loading ? "Saving..." : "Save Changes"}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-cancel"
                                                                    onClick={() => setIsEditingDetails(false)}
                                                                    disabled={loading}
                                                                    style={{ marginTop: '5px', borderRadius: '0px', fontSize: '12px', fontWeight: '600', height: '36px' }}>
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>




                                                </form>
                                            ) : (
                                                <>

                                                    <div className="row">
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">First Name</label>
                                                                <input type="text" className="form-control" value={firstName} disabled />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">Last Name</label>
                                                                <input type="text" className="form-control" value={lastName} disabled />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">Email</label>
                                                                <input type="email" className="form-control" value={email} disabled />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">Phone</label>
                                                                <input type="tel" className="form-control" value={phone || "Not provided"} disabled />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <button
                                                                className="btn btn-cancel"
                                                                onClick={() => setIsEditingDetails(true)}
                                                                style={{ marginTop: '27px', height: '36px', fontWeight: "500" }}>
                                                                Edit Details
                                                            </button>
                                                        </div>
                                                    </div>

                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-12 col-md-4 col-lg-4">
                                    {/* Change Password Section */}
                                    <div className="card border-0">
                                        <div className="card-body">
                                            <h5 className="card-title mb-3">Change Password</h5>
                                            {showPasswordForm ? (
                                                <form onSubmit={handleChangePassword}>
                                                    <div className="row">
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">Current Password</label>
                                                                <input
                                                                    type="password"
                                                                    className="form-control"
                                                                    value={currentPassword}
                                                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                                                    required
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">New Password</label>
                                                                <input
                                                                    type="password"
                                                                    className="form-control"
                                                                    value={newPassword}
                                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                                    required
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="mb-3">
                                                                <label className="form-label">Confirm New Password</label>
                                                                <input
                                                                    type="password"
                                                                    className="form-control"
                                                                    value={confirmPassword}
                                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                                    required
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col-sm-12 col-md-12 col-lg-12">
                                                            <div className="d-flex gap-2" style={{ marginTop: '26px' }}>
                                                                <button type="submit" className="btn btn-submit" disabled={loading}>
                                                                    {loading ? "Changing..." : "Change Password"}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-cancel"
                                                                    onClick={() => {
                                                                        setShowPasswordForm(false);
                                                                        setCurrentPassword("");
                                                                        setNewPassword("");
                                                                        setConfirmPassword("");
                                                                    }}
                                                                    disabled={loading}
                                                                    style={{ marginTop: '0px', borderRadius: '0px', fontSize: '12px', fontWeight: '600', height: '36px' }}>
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </form>
                                            ) : (
                                                <button
                                                    className="btn btn-cancel"
                                                    onClick={() => setShowPasswordForm(true)}
                                                >
                                                    Change Password
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-12 col-md-4 col-lg-4">
                                    {/* Profile Picture Section */}
                                    <div className="card mb-4 border-0" style={{ minHeight: '375px' }}>
                                        <div className="card-body">
                                            <h5 className="card-title mb-4">Profile Picture</h5>
                                            <div className="d-flex align-items-center mb-3 justify-content-center">
                                                {profilePicture ? (
                                                    <img
                                                        src={
                                                            profilePicture.startsWith("data:")
                                                                ? profilePicture
                                                                : `${envConfig.API_URL}/profishblue_media/profile_images/${profilePicture}`
                                                        }
                                                        alt="Profile"
                                                        className="rounded-circle me-3"
                                                        style={{ width: "189px", height: "189px", objectFit: "cover" }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="rounded-circle bg-secondary me-3 d-flex align-items-center justify-content-center"
                                                        style={{ width: "189px", height: "189px" }}
                                                    >
                                                        <span className="text-white">No Image</span>
                                                    </div>
                                                )}
                                                {!isEditingPicture && (
                                                    <button
                                                        className="btn mt-2"
                                                        onClick={() => setIsEditingPicture(true)}
                                                        style={{ position: 'relative', left: "-70px", background: '#58ab53', top: '70px', borderRadius: '23px', width: '40px' }}>
                                                        <img src="./assets/images/edit.svg" />
                                                    </button>
                                                )}
                                            </div>
                                            {isEditingPicture && (
                                                <form onSubmit={handleUploadPicture}>
                                                    <div className="mb-3">
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={handlePictureChange}
                                                            className="form-control"
                                                        />
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button type="submit" className="btn btn-submit" disabled={loading}>
                                                            {loading ? "Uploading..." : "Upload Picture"}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-cancel"
                                                            onClick={() => {
                                                                setIsEditingPicture(false);
                                                                setSelectedFile(null);
                                                                // Revert to original image if upload was not completed
                                                                const userDetails = JSON.parse(localStorage.getItem("userDetails"));
                                                                setProfilePicture(userDetails.profile_image || "default_user.png");
                                                            }}
                                                            disabled={loading}
                                                            style={{ marginTop: '0px', borderRadius: '0px', fontSize: '12px', fontWeight: '600', height: '36px' }} >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </form>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
}

export default Profile;